import { useEffect, useState } from 'react'
import { CloudUpload, X, Check, AlertCircle, Loader2 } from 'lucide-react'
import { useAuth } from '@/auth/useAuth'
import { getSupabaseClient } from '@/repository/supabase/client'
import {
  getRepository,
  getLedgerRepository,
  getRecurringExpenseRepository,
  getRecoveryAnchorRepository,
} from '@/repository'
import { useDebtStore } from '@/store/debtStore'
import { useLedgerStore } from '@/store/ledgerStore'
import { useRecurringExpenseStore } from '@/store/recurringExpenseStore'
import type {
  Debt,
  IncomeSource,
  MonthlyLedgerEntry,
  RecurringExpense,
  RecoveryAnchor,
} from '@/types'

const MIGRATED_FLAG = 'debtos:migratedAt:v1'

const LOCAL_KEYS = {
  debts: 'debtos:debts:v1',
  income: 'debtos:incomeSources:v1',
  ledger: 'debtos:ledger:v1',
  expenses: 'debtos:recurringExpenses:v1',
  anchor: 'debtos:recoveryAnchor:v1',
}

interface LocalSnapshot {
  debts: Debt[]
  income: IncomeSource[]
  ledger: MonthlyLedgerEntry[]
  expenses: RecurringExpense[]
  anchor: RecoveryAnchor | null
}

type Status = 'idle' | 'available' | 'dismissed' | 'importing' | 'done' | 'error'

/**
 * One-shot banner that surfaces after a fresh sign-in when:
 *   1. The user has local-mode data in localStorage, and
 *   2. The cloud account is empty for this user.
 *
 * Clicking "Import now" replays the local data into the four Supabase
 * repos via their `bulkReplace` paths. Clicking "Dismiss" sets the
 * `migratedAt` flag so the banner doesn't reappear next session. Either
 * way local data is preserved — we never delete the localStorage rows.
 *
 * The check is async and non-blocking — the banner only ever shows after
 * the cloud account verifiably has zero rows, so users with existing
 * cloud data are never asked to overwrite.
 */
export function MigrationPrompt() {
  const { isAuthenticated, workspaceId } = useAuth()
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [snapshot, setSnapshot] = useState<LocalSnapshot | null>(null)

  // Boot check: does the cloud account need a migration? Both early-
  // exits and intermediate transitions are kept out of synchronous
  // setState calls — the banner stays hidden until the async result
  // is known and `setStatus('available' | 'done' | 'error')` fires.
  // (See `react-hooks/set-state-in-effect`.)
  useEffect(() => {
    if (!isAuthenticated || !workspaceId) return

    // Already migrated / dismissed in a prior session.
    if (typeof localStorage !== 'undefined' && localStorage.getItem(MIGRATED_FLAG)) {
      return
    }

    let cancelled = false

    void (async () => {
      const local = readLocalSnapshot()
      const hasLocalData =
        local.debts.length > 0 ||
        local.income.length > 0 ||
        local.ledger.length > 0 ||
        local.expenses.length > 0 ||
        local.anchor !== null

      if (!hasLocalData) {
        // Nothing to migrate. Drop the flag so we don't re-check forever.
        try {
          localStorage.setItem(MIGRATED_FLAG, new Date().toISOString())
        } catch {
          /* non-fatal */
        }
        if (!cancelled) setStatus('idle')
        return
      }

      const empty = await isCloudAccountEmpty(workspaceId)
      if (cancelled) return

      if (!empty) {
        // Cloud already has data — assume user has used another device or
        // migrated via a different flow. Don't offer to overwrite.
        try {
          localStorage.setItem(MIGRATED_FLAG, new Date().toISOString())
        } catch {
          /* non-fatal */
        }
        setStatus('idle')
        return
      }

      setSnapshot(local)
      setStatus('available')
    })()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, workspaceId])

  const handleImport = async () => {
    if (!snapshot) return
    setStatus('importing')
    setErrorMsg(null)

    try {
      // Sequence matters for foreign-key sanity: parents before children.
      //   1. debts        (referenced by ledger.allocations)
      //   2. income       (referenced by ledger.income)
      //   3. ledger       (depends on debts + income)
      //   4. expenses     (standalone)
      //   5. anchor       (singleton, last)
      await getRepository().bulkReplace(snapshot.debts)
      await getLedgerRepository().bulkReplaceIncomeSources(snapshot.income)
      await getLedgerRepository().bulkReplaceLedgerEntries(snapshot.ledger)
      await getRecurringExpenseRepository().bulkReplace(snapshot.expenses)
      if (snapshot.anchor) {
        await getRecoveryAnchorRepository().set(snapshot.anchor)
      }

      // Mark migrated and reset stores so the UI re-fetches from cloud.
      localStorage.setItem(MIGRATED_FLAG, new Date().toISOString())
      useDebtStore.getState().reset()
      useLedgerStore.getState().reset()
      useRecurringExpenseStore.getState().reset()

      setStatus('done')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err))
      setStatus('error')
    }
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(MIGRATED_FLAG, new Date().toISOString())
    } catch {
      /* non-fatal */
    }
    setStatus('dismissed')
  }

  if (status === 'idle' || status === 'dismissed') return null

  return (
    <div className="bg-blue-500/10 border-b border-blue-500/30">
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 py-3 flex items-start gap-3">
        {status === 'done' ? (
          <Check className="w-5 h-5 shrink-0 text-emerald-400 mt-0.5" />
        ) : status === 'error' ? (
          <AlertCircle className="w-5 h-5 shrink-0 text-red-400 mt-0.5" />
        ) : (
          <CloudUpload className="w-5 h-5 shrink-0 text-blue-400 mt-0.5" />
        )}

        <div className="flex-1 min-w-0 text-sm">
          {status === 'available' && snapshot ? (
            <>
              <div className="font-medium text-white">Import your local data?</div>
              <div className="text-slate-300 text-xs mt-0.5">
                We found {summariseSnapshot(snapshot)} saved on this device. Copy it into your cloud
                account so it syncs across devices?
              </div>
            </>
          ) : null}

          {status === 'importing' ? (
            <div className="flex items-center gap-2 text-white">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Importing your data to the cloud…</span>
            </div>
          ) : null}

          {status === 'done' ? (
            <div className="text-emerald-300">
              <span className="font-medium text-white">Imported.</span> Your data is now syncing
              across devices.
            </div>
          ) : null}

          {status === 'error' ? (
            <>
              <div className="font-medium text-white">Import failed</div>
              <div className="text-red-200/80 text-xs mt-0.5">
                {errorMsg ?? 'Something went wrong.'} Your local data is still safe — try again or
                contact support.
              </div>
            </>
          ) : null}
        </div>

        {status === 'available' ? (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs text-slate-300 hover:text-white"
            >
              Not now
            </button>
            <button
              onClick={handleImport}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium"
            >
              Import now
            </button>
          </div>
        ) : null}

        {status === 'done' || status === 'error' ? (
          <button
            onClick={() => setStatus('dismissed')}
            className="p-1 text-slate-400 hover:text-white shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        ) : null}

        {status === 'error' ? (
          <button
            onClick={handleImport}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-medium shrink-0"
          >
            Retry
          </button>
        ) : null}
      </div>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────

function readLocalSnapshot(): LocalSnapshot {
  return {
    debts: readJson<Debt[]>(LOCAL_KEYS.debts, []),
    income: readJson<IncomeSource[]>(LOCAL_KEYS.income, []),
    ledger: readJson<MonthlyLedgerEntry[]>(LOCAL_KEYS.ledger, []),
    expenses: readJson<RecurringExpense[]>(LOCAL_KEYS.expenses, []),
    anchor: readJson<RecoveryAnchor | null>(LOCAL_KEYS.anchor, null),
  }
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

/**
 * Verifies no rows exist for this workspace across all four entity tables.
 * Uses `head + count: 'exact'` to skip row materialisation; only the count
 * comes back.
 */
async function isCloudAccountEmpty(workspaceId: string): Promise<boolean> {
  const client = getSupabaseClient()
  if (!client) return false

  const tables: Array<'debts' | 'income_sources' | 'ledger_entries' | 'recurring_expenses'> = [
    'debts',
    'income_sources',
    'ledger_entries',
    'recurring_expenses',
  ]

  for (const table of tables) {
    const { count, error } = await client
      .from(table)
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
    if (error) {
      console.warn(`[migration] Failed to count ${table}:`, error.message)
      return false
    }
    if ((count ?? 0) > 0) return false
  }
  return true
}

function summariseSnapshot(s: LocalSnapshot): string {
  const parts: string[] = []
  if (s.debts.length > 0) parts.push(`${s.debts.length} debt${s.debts.length === 1 ? '' : 's'}`)
  if (s.ledger.length > 0) {
    parts.push(`${s.ledger.length} month${s.ledger.length === 1 ? '' : 's'} of history`)
  }
  if (s.expenses.length > 0) {
    parts.push(`${s.expenses.length} recurring expense${s.expenses.length === 1 ? '' : 's'}`)
  }
  if (s.income.length > 0) {
    parts.push(`${s.income.length} income source${s.income.length === 1 ? '' : 's'}`)
  }
  if (s.anchor) parts.push('your recovery anchor')
  if (parts.length === 0) return 'some data'
  if (parts.length === 1) return parts[0] ?? ''
  if (parts.length === 2) return parts.join(' and ')
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`
}
